import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { CORPORATE_POLICY_PATH } from "../constants";
import { IPolicyBuilderConfig } from "../types/builder";
import {
  CreateCorporatePolicyDto,
  CreateCorporatePolicyRuleDto,
  PatchCorporatePolicyStatusDto,
  UpdateCorporatePolicyDto,
} from "../dtos/corporatePolicy.dtos";
import { formatString } from "@/core/utils/helpers";

export default class CorporatePolicyService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public getBuilderConfig(
    organizationId: string,
  ): Promise<ICoreResponse<IPolicyBuilderConfig>> {
    return this.restClient.get(
      formatString(CORPORATE_POLICY_PATH.GET_BUILDER_CONFIG, organizationId),
    );
  }

  public getCorporatePolicies(organizationId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        CORPORATE_POLICY_PATH.GET_CORPORATE_POLICIES,
        organizationId,
      ),
    );
  }

  public getCorporatePoliciesByOrganizationUser(
    organizationUserId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        CORPORATE_POLICY_PATH.GET_CORPORATE_POLICY_BY_USER,
        organizationUserId,
      ),
    );
  }

  public getCorporatePolicyById(
    corporatePolicyId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(
        CORPORATE_POLICY_PATH.GET_CORPORATE_POLICY_BY_ID,
        corporatePolicyId,
      ),
    );
  }

  public getOptionSourceOptions(
    resolvedPath: string,
    params?: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ): Promise<ICoreResponse> {
    return this.restClient.get(resolvedPath, params, {
      signal: options?.signal,
    });
  }

  public createCorporatePolicy(
    organizationId: string,
    body: CreateCorporatePolicyDto,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        CORPORATE_POLICY_PATH.CREATE_CORPORATE_POLICY,
        organizationId,
      ),
      body,
    );
  }

  public createCorporatePolicyRules(
    corporatePolicyId: string,
    body: CreateCorporatePolicyRuleDto,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(
        CORPORATE_POLICY_PATH.CREATE_CORPORATE_POLICY_RULES,
        corporatePolicyId,
      ),
      body,
    );
  }

  public updateCorporatePolicy(
    organizationId: string,
    corporatePolicyId: string,
    body: UpdateCorporatePolicyDto | PatchCorporatePolicyStatusDto,
  ): Promise<ICoreResponse> {
    return this.restClient.patch(
      formatString(
        CORPORATE_POLICY_PATH.UPDATE_CORPORATE_POLICY,
        organizationId,
        corporatePolicyId,
      ),
      body,
    );
  }

  public deleteCorporatePolicy(
    organizationId: string,
    corporatePolicyId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.delete(
      formatString(
        CORPORATE_POLICY_PATH.DELETE_CORPORATE_POLICY,
        organizationId,
        corporatePolicyId,
      ),
      "",
    );
  }

  public patchCorporatePolicyStatus(
    organizationId: string,
    corporatePolicyId: string,
    isActive: boolean,
  ): Promise<ICoreResponse> {
    return this.updateCorporatePolicy(organizationId, corporatePolicyId, {
      is_active: isActive,
    });
  }

  public updateCorporatePolicyRules(
    rulePublicId: string,
    body: CreateCorporatePolicyRuleDto,
  ): Promise<ICoreResponse> {
    return this.restClient.patch(
      formatString(
        CORPORATE_POLICY_PATH.UPDATE_CORPORATE_POLICY_RULES,
        rulePublicId,
      ),
      body,
    );
  }

  public deleteCorporatePolicyRules(
    organizationId: string,
    corporatePolicyId: string,
    rulePublicId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.delete(
      formatString(
        CORPORATE_POLICY_PATH.DELETE_CORPORATE_POLICY_RULES,
        organizationId,
        corporatePolicyId,
        rulePublicId,
      ),
      "",
    );
  }
}
